'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@components/Navbar';
import Footer from '@components/footer/Footer';
import { Card, CardHeader, CardContent, Badge, Button, Separator } from '@/components/ui';
import { Calendar, BookOpen, MessageSquare, Star, Edit3, GraduationCap } from 'lucide-react';
import { generateSlug } from '@/lib/slugify';

// Helper function to convert card image URLs to hero image URLs
const getHeroImageUrl = (imageUrl) => {
  if (!imageUrl) return imageUrl;
  if (imageUrl.includes('/card/') && imageUrl.includes('_250x200')) {
    return imageUrl
      .replace('/card/', '/hero/')
      .replace('_250x200', '_500xauto');
  }
  return imageUrl;
};

// Helper function to get initials for avatar fallback
const getInitials = (name) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// Helper function to format date
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Helper function to render star rating
const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < rating ? "fill-yellow-400 text-yellow-400" : "fill-gray-200 text-gray-200"}`}
        />
      ))}
    </div>
  );
};

export default function PublicProfilePageClient({ profileData }) {
  const { userData, teachers, currentCourses, userReviews } = profileData;
  const { name, description, createdAt, classesTaken, reviewsCount, avatarUrl, isOwnProfile } = userData;

  const joinedDate = formatDate(createdAt);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header>
        <Navbar />
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Profile Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="h-24 w-24 mx-auto mb-4 relative rounded-full overflow-hidden bg-blue-100">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-blue-100 text-blue-700 text-lg font-semibold"
                      style={{ display: avatarUrl ? 'none' : 'flex' }}
                    >
                      {getInitials(name || 'User')}
                    </div>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-2">{name}</h2>

                  {isOwnProfile && (
                    <Button variant="outline" size="sm" className="mb-4 bg-transparent">
                      <Edit3 className="h-4 w-4 mr-2" />
                      <a href="/settings" className="text-inherit">Edit Profile</a>
                    </Button>
                  )}

                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">{description}</p>

                  <Separator className="mb-6" />

                  {/* Stats */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span className="text-sm">Member since</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{joinedDate}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <BookOpen className="h-4 w-4 mr-2" />
                        <span className="text-sm">Classes taken</span>
                      </div>
                      <Badge variant="secondary">{classesTaken}</Badge>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <MessageSquare className="h-4 w-4 mr-2" />
                        <span className="text-sm">Reviews written</span>
                      </div>
                      <Badge variant="secondary">{reviewsCount}</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-8">
            {/* Teachers Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">My Teachers</h3>
                </div>
              </CardHeader>
              <CardContent>
                {(!teachers || teachers.length === 0) ? (
                  <p className="text-gray-500 text-center py-8">No teachers yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {teachers.map((teacher) => (
                      <Link
                        key={teacher.id}
                        href={`/teacher-profile/${teacher.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="h-12 w-12 relative rounded-full overflow-hidden bg-blue-100 flex-shrink-0">
                          {teacher.image ? (
                            <img
                              src={teacher.image}
                              alt={teacher.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-blue-100 text-blue-700 text-sm font-medium"
                            style={{ display: teacher.image ? 'none' : 'flex' }}
                          >
                            {getInitials(teacher.name || 'Teacher')}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{teacher.name || 'No Name'}</h4>
                          <p className="text-sm text-gray-600">{teacher.subject || 'Subject not specified'}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Current Classes Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Current Classes</h3>
                </div>
              </CardHeader>
              <CardContent>
                {(!currentCourses || currentCourses.length === 0) ? (
                  <p className="text-gray-500 text-center py-8">No classes enrolled yet.</p>
                ) : (
                  <div className="space-y-6">
                    {currentCourses.map((course) => (
                      <Link
                        key={course.id}
                        href={`/classes/${generateSlug(course.name, course.id)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col md:flex-row gap-4 p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-md transition-all cursor-pointer"
                      >
                        <img
                          src={getHeroImageUrl(course.imageUrl) || '/images/class.png'}
                          alt={course.name}
                          className="w-full md:w-48 h-32 object-contain rounded-lg bg-gray-50"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-2">{course.name || 'Untitled Course'}</h4>
                          <p className="text-gray-600 text-sm mb-3 leading-relaxed">{course.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">by {course.teacher?.name || 'Unknown Instructor'}</span>
                            {course.progress && (
                              <div className="flex items-center gap-2">
                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-green-500 h-2 rounded-full"
                                    style={{ width: `${course.progress}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium text-gray-700">{course.progress}%</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reviews Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">My Reviews</h3>
                </div>
              </CardHeader>
              <CardContent>
                {(!userReviews || userReviews.length === 0) ? (
                  <p className="text-gray-500 text-center py-8">No reviews yet.</p>
                ) : (
                  <div className="space-y-6">
                    {userReviews.map((review) => (
                      <div key={review.id} className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">{review.courseName || review.course?.name || 'Unknown Course'}</h4>
                            <StarRating rating={review.rating} />
                          </div>
                          <span className="text-sm text-gray-500">{formatDate(review.postedAt)}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{review.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}